import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { tintVTOService } from "services/tintvto"
import { useAlert } from "providers/AlertProvider"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { ProductCategories, ProductCategoriesLink } from "@/components/Navbar/types"
import { IntegrationType, Product, ProductV2 } from "types"
import { ProductType } from "store/product/types"
import { TIENDANUBE_PRODUCTS_QUERY_KEY } from "./useTiendanubeQuery"
import { opinionService } from "@/features/leaveOpition/LeaveOpinionFeatureService"
import { useAppSelector } from "hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"
import { useTintVTOAxiosService } from "services/axiosService"

export const PRODUCTS_QUERY_KEY = (
  page?: number,
  searchText?: string,
  category?: ProductCategories,
  merchantId?: string
) =>
  page === undefined &&
  searchText === undefined &&
  category === undefined &&
  merchantId === undefined
    ? ["products"]
    : [
        "products",
        {
          page,
          searchText,
          category,
          merchantId,
        },
      ]

export const ALL_PRODUCTS_QUERY_KEY = ["allProducts"]

export type ProductsQueryParams = {
  page: number
  searchText: string
  category: ProductCategories
  merchantId: string
}

type ProductsQueryResponse = {
  items: Product[]
  pageCount: number
}

export const useProductClientClearCache = () => {
  const queryClient = useQueryClient()
  return () => {
    queryClient.resetQueries({ queryKey: PRODUCTS_QUERY_KEY() })
    queryClient.resetQueries({ queryKey: ALL_PRODUCTS_QUERY_KEY })
  }
}

export const useProductsQuery = ({
  page,
  searchText,
  category,
  merchantId,
}: ProductsQueryParams) => {
  const { showAlert } = useAlert()
  const { t } = useTranslation()

  return useQuery<ProductsQueryResponse>({
    queryKey: PRODUCTS_QUERY_KEY(page, searchText, category, merchantId),
    queryFn: async () => {
      try {
        const { data } = await tintVTOService.productsV2(page - 1, searchText, category, merchantId)
        return {
          items: data?.productsV2?.items || [],
          pageCount: data?.productsV2?.pageInfo?.pageQty || 0,
        }
      } catch (error) {
        showAlert({ error: true, text: t("productsPage.fetchError") })
        throw error
      }
    },
  })
}

export const useAllProductsQuery = (merchantId: string) => {
  const { showAlert } = useAlert()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const clearCache = useProductClientClearCache()

  const query = useQuery<ProductType[]>({
    queryKey: ALL_PRODUCTS_QUERY_KEY,
    queryFn: async () => {
      try {
        const makeup = await tintVTOService.fetchAllProducts(merchantId, ProductCategories.MAKEUP)
        const glasses = await tintVTOService.fetchAllProducts(merchantId, ProductCategories.GLASSES)
        queryClient.invalidateQueries({ queryKey: TIENDANUBE_PRODUCTS_QUERY_KEY })
        return [...makeup, ...glasses]
      } catch (error) {
        showAlert({ error: true, text: t("productsPage.fetchError") })
        throw error
      }
    },
  })
  return {
    ...query,
    clearCache,
  }
}

export const useSaveProductMutation = (
  merchantId: string,
  category: ProductCategories,
  onAfterSave?: (productId: string, skuIds: string[], translations: any) => Promise<void>
) => {
  const clearCache = useProductClientClearCache()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { showAlert } = useAlert()
  const userInfo = useAppSelector(selectUserInfo)
  const axiosService = useTintVTOAxiosService()

  return useMutation({
    mutationFn: async (product: ProductV2 & { pendingTranslations?: any }) => {
      const [response] = await Promise.all([
        tintVTOService.saveProductV2(product),
        opinionService.completeInitialSetup(
          userInfo,
          category === ProductCategories.GLASSES ? "glasses" : "makeup"
        ),
      ])
      if (response.errors) throw new Error(response.errors[0].message)

      if (
        (category === ProductCategories.GLASSES || category === ProductCategories.MAKEUP) &&
        !product.id &&
        userInfo.integrationType === IntegrationType.TIENDANUBE
      ) {
        const skus = product.colors?.map((color) => color.sku).filter(Boolean) || []
        if (skus.length > 0) await axiosService.updateMerchantSkus(merchantId, skus, [])
      }

      // Get productId from response
      // saveProductV2 returns the product ID directly (as a number or string)
      const responseId = response.data?.saveProductV2
      let savedProductId = 
        (responseId && String(responseId)) || 
        (response.data?.saveProductV2 as any)?.id || 
        product.id

      // Save translations if we have a product ID and translations to save
      if (onAfterSave && product.pendingTranslations && savedProductId) {
        const skuIds =
          product.colors?.map((c) => c.skuId || c.sku).filter(Boolean) || []
        await onAfterSave(savedProductId, skuIds, product.pendingTranslations)
      }

      return { ...response, savedProductId }
    },
    onSuccess: (response, product) => {
      showAlert({
        error: false,
        text: t("productPage.saveSuccess", {
          name: product.name,
          action: product.id ? t("productPage.updated") : t("productPage.created"),
        }),
      })
      clearCache()
      navigate(ProductCategoriesLink[category])
    },
    onError: (error) => {
      showAlert({
        error: true,
        text: error.message || t("productPage.saveError"),
      })
    },
  })
}

export const useToggleProductVisibilityMutation = ({
  page,
  searchText,
  category,
  merchantId,
}: ProductsQueryParams) => {
  const queryClient = useQueryClient()
  const clearCache = useProductClientClearCache()
  const { showAlert } = useAlert()
  const { t } = useTranslation()
  const userInfo = useAppSelector(selectUserInfo)
  const axiosService = useTintVTOAxiosService()

  return useMutation({
    mutationFn: async ({ id, visible, skus }: Product) => {
      const response = await tintVTOService.toggleProductVisibility(id, !visible)
      if (response.errors) throw new Error(response.errors[0].message)

      if (
        (category === ProductCategories.GLASSES || category === ProductCategories.MAKEUP) &&
        userInfo.integrationType === IntegrationType.TIENDANUBE
      ) {
        const productSkus = skus?.map((sku) => sku.sku).filter(Boolean) || []
        if (productSkus.length > 0) {
          if (!visible) {
            await axiosService.updateMerchantSkus(merchantId, [], productSkus)
          } else {
            await axiosService.updateMerchantSkus(merchantId, productSkus, [])
          }
        }
      }

      return response
    },
    onMutate: async ({ id, visible }) => {
      await queryClient.cancelQueries({ queryKey: PRODUCTS_QUERY_KEY() })

      const previousProducts = queryClient.getQueryData<Product[]>(
        PRODUCTS_QUERY_KEY(page, searchText, category, merchantId)
      )

      if (previousProducts)
        queryClient.setQueryData<ProductsQueryResponse>(
          PRODUCTS_QUERY_KEY(page, searchText, category, merchantId),
          (old) => ({
            items: old.items?.map((product) =>
              product.id === id ? { ...product, visible: !visible } : product
            ),
            pageCount: old.pageCount,
          })
        )

      return { previousProducts }
    },
    onSuccess: (_, { visible }) => {
      showAlert({
        error: false,
        text: t("productsPage.visibilityToggle", {
          productName: "Product",
          visibility: !visible ? t("productsPage.hidden") : t("productsPage.visible"),
        }),
      })
      clearCache()
    },
    onError: (error, variables, context) => {
      showAlert({ error: true, text: error.message || t("productsPage.visibilityToggleError") })
      if (context?.previousProducts)
        queryClient.setQueryData(
          PRODUCTS_QUERY_KEY(page, searchText, category, merchantId),
          context.previousProducts
        )
    },
  })
}

export const useDeleteProductMutation = ({
  page,
  searchText,
  category,
  merchantId,
}: ProductsQueryParams) => {
  const queryClient = useQueryClient()
  const clearCache = useProductClientClearCache()
  const { showAlert } = useAlert()
  const { t } = useTranslation()
  const userInfo = useAppSelector(selectUserInfo)
  const axiosService = useTintVTOAxiosService()

  return useMutation({
    mutationFn: async (product: ProductV2) => {
      const response = await tintVTOService.deleteProduct(product.id)
      if (response.errors) throw new Error(response.errors[0].message)

      if (
        (category === ProductCategories.GLASSES || category === ProductCategories.MAKEUP) &&
        userInfo.integrationType === IntegrationType.TIENDANUBE
      ) {
        const skus = product.colors?.map((color) => color.sku).filter(Boolean) || []
        if (skus.length > 0) await axiosService.updateMerchantSkus(merchantId, [], skus)
      }

      return response
    },
    onMutate: async (product) => {
      const queryKey = PRODUCTS_QUERY_KEY(page, searchText, category, merchantId)
      await queryClient.cancelQueries({ queryKey })

      const previousProducts = queryClient.getQueryData<Product[]>(queryKey)

      if (previousProducts)
        queryClient.setQueryData<ProductsQueryResponse>(queryKey, (old) => ({
          items: old.items?.filter((p) => p.id !== product.id),
          pageCount: old.pageCount,
        }))

      return { previousProducts }
    },
    onSuccess: () => {
      showAlert({
        error: false,
        text: t("productsPage.deleteSuccess", { productName: "Product" }),
      })
      clearCache()
    },
    onError: (error, variables, context) => {
      showAlert({ error: true, text: error.message || t("productsPage.deleteError") })
      if (context?.previousProducts)
        queryClient.setQueryData(PRODUCTS_QUERY_KEY(), context.previousProducts)
    },
  })
}
